<template>
    <div class="container">

        <CommonBreadcrumb class="bread_block" />  

        <CommonTitleDescription :title="tags.seo.value.page_title" :description="tags.seo.value.page_description" :total="resultCount" />

        <div class="min_height">
            <CommonJewelryFilter :page="'shop'" :viewType="viewType" @changeViewType="changeViewType" />
            <div class="loader_box_mod" v-show="showSkeleton">
                <img width="200" loading="lazy" height="200" alt="loader" src="~/assets/assets/loader.svg">
            </div>
            <CommonNoResultFound class="mt-1 pt-1" v-if="displayNoResults" :type="'no_result'" />
            <CommonNoResultFound class="mt-1 pt-1" v-if="hasError" :type="'client_network_error'" />

            <TransitionGroup appear v-show="!showSkeleton" name="prod_box_animation" tag="div" class="row listing-container-min-height">
                <template v-for="data in results" :key="data.uniqueKey">
                    <ReturnProductBox 
                        v-if="['in-stock-ring', 'in-stock-earring', 'in-stock-pendant'].includes(moduleSubType)" 
                        :data="data" 
                        :viewType="viewType"
                        :style="{ transitionDelay: `${data.transitionDelayKey * 30}ms` }"
                    />
                    <PresetProductBox 
                        v-else-if="moduleType === 'preset'" 
                        :data="data" 
                        :viewType="viewType" 
                        :style="{ transitionDelay: `${data.transitionDelayKey * 30}ms` }"
                    />
                    
                    <JewelryProductBox 
                        v-else
                        :viewType="viewType"
                        :type="'cyor'" 
                        :data="data"  
                        :sub_module="moduleSubType" 
                        :style="{ transitionDelay: `${data.transitionDelayKey * 30}ms` }"
                    />
                </template>
            </TransitionGroup>

            <div class="loader_box_mod" v-show="showMoreSkeleton">
                <img width="200" loading="lazy" height="200" alt="loader" src="~/assets/assets/loader.svg">
            </div>

            <div v-if="getSettingListingNextPageUrl" class="text-center mx-auto my-5 more_data_box">
                <div class="show_more_btn" @click="showMore()">SHOW MORE</div>
            </div>
        </div>


        <CommonH2TitleDescription :title="tags.seo.value.h2_title" :description="tags.seo.value.h2_description" /> 

        <FaqDynamicFaq class="mt-4 pt-4" />

    </div>
</template>

<script setup>
    import { cyolistStore }  from '@/stores/cyolist/cyolist';
    import { useSeo } from '@/composables/seo/setMetaTags'

    const store = cyolistStore();
    const tags = useSeo();
    const nuxtApp = useNuxtApp()

    const route = useRoute();
    const router = useRouter();

    const {$jewelryImpressionGtag} = useNuxtApp()
    
    const moduleType = route.meta.moduleType
    const moduleSubType = route.meta.moduleSubType 

    
    const viewType = ref('grid')

    let fAttributes = [
        {
            name:'metal',
            label:'Metal',
            urlLabel:'Metal',
            type:'select', 
            hasIcon:true,
            iconBasePath:'/assets/metals/',
            iconType:'.png',
            singleSelect:true,
            default:null,
            isOpen:false
        },
        {
            name:'style',
            label:'Style',
            urlLabel:'Style',
            type:'select', 
            hasIcon:true,
            iconBasePath:'/assets/metals/',
            iconType:'.png',
            singleSelect:false,
            default:null,
            isOpen:false
        },
        {
            name:'category',
            label:'Category',
            urlLabel:'Category',
            type:'select', 
            hasIcon:true,
            iconBasePath:'/assets/metals/',
            iconType:'.png',
            singleSelect:false,
            default:null,
            isOpen:false
        },
        {
            name:'shape',
            label:'Shape',
            urlLabel:'Shape',
            type:'select', 
            hasIcon:true,
            iconBasePath:'/assets/metals/',
            iconType:'.png',
            singleSelect:true,
            default:null,
            isOpen:false
        },
        {
            name:'carat',
            label:'Carat',
            urlLabel:'Carat',
            type:'select', 
            hasIcon:true,
            iconBasePath:'/assets/metals/',
            iconType:'.png',
            singleSelect:true,
            default:null,
            isOpen:false
        },
        {
            name:'sort',
            label:'Sort',
            urlLabel:'Sort',
            type:'select', 
            displaySelected:true, 
            hasIcon:false,
            iconBasePath:'',
            iconType:'.svg',
            singleSelect:true,
            isOpen:false,
            default:'featured'
        },
        {
            name:'shipdate',
            label:'Shipdate',
            urlLabel:'Shipdate',
            type:'select', 
            displaySelected:true, 
            hasIcon:false,
            iconBasePath:'',
            iconType:'.svg',
            singleSelect:true,
            isOpen:false,
            default: ''
        },
        {
            name:'price',
            label:'Price',
            urlLabel:'Price',
            type:'slider', 
            displaySelected:true, 
            singleSelect:true,
            default:null,
            isOpen:false,
            config:{step:1, type: 'num', format:{notation:'$', position:'prepend'}}
        }
    ]

    const results = computed(() => {
        const item = store.getSettingListingResult()
        if(import.meta.client && item && item.length){
            $jewelryImpressionGtag(item)
        }
        return item
    })

    const resultCount = computed(() => store.getSettingListingResultCount())

    const getSettingListingNextPageUrl = computed(() => store.getSettingListingNextPageUrl())

    const displayNoResults = computed(() => {
        return store.getSettingListingCurrentPage() === 1 && results.value.length === 0
    })

    const hasError = computed(() => {
        return store.getListingError()
    })
    const showSkeleton = computed(()=>store.getSettingShowSkeleton() )

    const showMoreSkeleton = computed(() => {
        return store.getSkeletonShowMore()
    })

    function showMore(){
        store.toggleSettingListingShowMore({ type: moduleSubType})
    }

    
    function changeViewType(type){
        viewType.value = type
    }
    

    let module = 'shop'
    if(moduleType === 'preset'){
        module = 'preset'
    }

    const coupleArr = ['metal', 'sort', 'price', 'shipdate']
    const anniversaryArr = ['metal', 'style', 'carat', 'sort', 'shape', 'shipdate']
    const toiArr = ['metal', 'carat', 'sort', 'price', 'shape', 'shipdate']
    const presetArr = ['metal', 'style', 'shape', 'carat', 'sort', 'price', 'shape']

    const allArr = ['metal', 'style', 'sort', 'price', 'shape', 'shipdate']
    const bandArr = ['metal', 'style', 'sort', 'price', 'shape', 'shipdate']
    const restArr = ['metal', 'carat', 'sort', 'price', 'shape', 'shipdate']
    const instock = ['metal', 'sort', 'price', 'shape','shipdate']
    const instock_ring = ['metal', 'sort', 'price', 'style', 'shape', 'shipdate']

    if( moduleSubType === 'couple-bands-ring' ){
            
        fAttributes = fAttributes.filter(attribute => coupleArr.includes(attribute.name));
    }

    

    if( moduleSubType === 'band-ring' ){

        fAttributes = fAttributes.filter(attribute => bandArr.includes(attribute.name));
        
    }


    if( moduleSubType === 'anniversary-ring' ){

        fAttributes = fAttributes.filter(attribute => anniversaryArr.includes(attribute.name));

    }


    if( moduleSubType === '' ){

        fAttributes = fAttributes.filter(attribute => allArr.includes(attribute.name));
        
    }
    
    if( ['full-eternity-ring', 'stud-earring', 'hoop-earring', 'tennis-bracelet', 'fashion-bracelet','fashion-earring','tennis-necklace', 'solitaire-necklace'].includes(moduleSubType) ){
        fAttributes = fAttributes.filter(attribute => restArr.includes(attribute.name));
    }

    if( moduleSubType === 'toi-et-moi-ring' ){
        fAttributes = fAttributes.filter(attribute => toiArr.includes(attribute.name));
    }

    if( moduleSubType === 'preset' ){

        fAttributes = fAttributes.filter(attribute => presetArr.includes(attribute.name));
    }

    if( ['in-stock-earring', 'in-stock-pendant'].includes(moduleSubType) ){

        fAttributes = fAttributes.filter(attribute => instock.includes(attribute.name));
    }
    
    if( ['in-stock-ring'].includes(moduleSubType) ){

        fAttributes = fAttributes.filter(attribute => instock_ring.includes(attribute.name));
        
    }
    
    const settings = { moduleType:module, moduleSubType:moduleSubType, filters : fAttributes, page:'setting' }






    // start
    const initialPageCallback = async ( asyncData ) => {
        if( asyncData  ){

            return await Promise.all( [
                tags.setMetaTags(),
                useAsyncData( route.fullPath , () => store.setupCyoSettingFilter(settings, route, router).then( () => true ).catch( (error) => console.log('error', error) ))
            ])
        
        }else{

            return await Promise.all( [
                tags.setMetaTags(),
                store.setupCyoSettingFilter(settings, route, router).then( () => true )
            ])
        }

    }

    if( import.meta.client ){

        // Make RefreshKey reactive using computed
        const refreshKey = computed(() => route.query.RefreshKey);

        // watch works directly on a ref
        watch(refreshKey, async () => {
            
            console.log('reffesh called', refreshKey)
            // await refreshNuxtData()
            initialPageCallback()
        })

        const refreshKeyTwo = computed(() => route.params.start_with);

        // watch works directly on a ref
        watch(refreshKeyTwo, async ( nv, ov ) => {

            if( typeof nv == 'string'  ){
                initialPageCallback()
            }
        })
    }
    
    const isHydratedOnServer =  (import.meta.client && nuxtApp.isHydrating && nuxtApp.payload.serverRendered)

    // reset the store on client side page navigations
    if( !isHydratedOnServer ) {

        await store.$reset()
    }   


    usePopstateHandler(async () => {
        initialPageCallback(false)
    });

    if( import.meta.client ){

        initialPageCallback(true)
    }else{

        await initialPageCallback( true)
    }
    // end
</script>


<style scoped>
    .min_height{
        min-height: 60vh;
    }
    .loader_box {
        /* height: 45vh; */
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .loader_box_mod {
        height: 45vh;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .more_data_box {
        width: fit-content;
    }

    .show_more_btn {
        border: 1px solid #000;
        border-radius: 4px;
        letter-spacing: 1.4px;
        font-size: 14px;
        font-weight: 600;
        margin: 0 auto;
        padding: 11px 25px;
        text-transform: uppercase;
        width: 100%;
        cursor: pointer;
    }
</style>